import GoToService from '@/router/GoToService';
import { MessageDataObject } from './useIframeConnection';
import { Routes } from '@/router/Routes';
import AppRouter from '@/router';
import { ModalService } from '@/components/fpComponents/services/ModalService/ModalService';
import { useNewDealModal } from './useNewDealModal';
import { useNewClientModal } from './useNewClientModal';
import { DealCreationMode } from '@/models/Deal';
import { useModalStore } from '@/components/L/Modal/useModalStore';
import {
  type CartAddNewDealPayload,
  type CartEditClientDataPayload,
  type CartGoToClientDealsPayload,
  IFRAME_MESSAGE_TYPES,
  iframeBridge,
} from './iframeBridgeProtocol';
import { useIframeBridge } from './useIframeBridge';
import { clientTypeFromIsCompany } from './iframeModalQuery';

export const useCartModal = (modalService?: ModalService) => {
  const iframeMessageCallback = async (
    eventData: MessageDataObject<
      | CartGoToClientDealsPayload
      | CartAddNewDealPayload
      | CartEditClientDataPayload
    >
  ) => {
    const { toLendiCrm } = useIframeBridge();

    if (iframeBridge.toFincrm.cart.goToClientDeals.matches(eventData) && eventData?.data?.clientId) {
      GoToService.client(Number(eventData.data.clientId));
      const {closeIframeModal} = useModalStore();
      closeIframeModal(IFRAME_MESSAGE_TYPES.CART_MODAL, AppRouter.currentRoute.name === Routes.CLIENTS.CLIENT_DETAILS || null);
    } else if (iframeBridge.toFincrm.cart.addNewDeal.matches(eventData) && eventData?.data?.clientId) {
      const { openNewDealModal } = useNewDealModal(modalService);
      const modalResponse = await openNewDealModal(Number(eventData.data.clientId));
      toLendiCrm.cart.refreshAfterDealCreated(modalResponse || null);
    } else if (iframeBridge.toFincrm.cart.editClientData.matches(eventData)) {
      const { openAddClientModal } = useNewClientModal(modalService);
      const modalResponse = await openAddClientModal({
        clientType: clientTypeFromIsCompany(eventData.data?.isCompany),
        parentModalType: IFRAME_MESSAGE_TYPES.CART_MODAL,
        mode: eventData.data?.mode,
        editingFields: eventData.data?.editingFields,
      });
      toLendiCrm.cart.applyEditedClientData(modalResponse || null);
    }
  };

  const openCartModal = async (clientId: string | number, draftId?: string | number, mode?: DealCreationMode) => {
    let cartModalUrl = GoToService.getLendiV3Url(`deals/carts/${clientId}/${draftId}`, true);
    if (mode) {
      const tokenQuerySeparator = cartModalUrl.includes('?') ? '&' : '?';
      cartModalUrl += `${tokenQuerySeparator}mode=${encodeURIComponent(mode)}`;
    }

    const cartModalResponse = await modalService?.openIframeModal({
      url: cartModalUrl,
      name: IFRAME_MESSAGE_TYPES.CART_MODAL,
      callback: iframeMessageCallback,
    }, {
      fullscreen: true,
      persistent: true,
    });
    
    return cartModalResponse;
  };

  return {
    iframeMessageCallback,
    openCartModal
  };
};
